import { Alert, Button } from 'antd';
import { useMapLayerAttributes } from '../../components/useMapLayerAttributes.ts';
import { getMapLayerTypeLabel } from '../../layers/MapLayer.ts';
import { isMetadataSupportedLayerType } from '../../metadata/fetchLayerMetadata.ts';
import { useMapServiceTypeValidation } from '../detection/useMapServiceTypeValidation.ts';
import { useLayerAttributeReconciliation } from '../metadata/useLayerAttributeReconciliation.ts';
import { useWatchedMapLayerSource } from '../useWatchedMapLayerSource.ts';

export interface IMapLayerServiceStatusProps {
  prefix: string;
}

const STATUS_STYLE = { marginBottom: 16 };

/**
 * Area di feedback unica del servizio configurato, mostrata sotto il
 * campo URL del layer. Combina due segnali:
 *
 * - **Introspezione** (tipi vettoriali): attributi disponibili,
 *   riconciliazione degli attributi configurati non più esistenti,
 *   errori di raggiungibilità.
 * - **Verifica del tipo di servizio** (famiglia ArcGIS, WMS, WFS):
 *   controlla che il servizio risponda davvero come il tipo scelto.
 *
 * La verifica non è mai bloccante: il salvataggio resta possibile.
 * Per i tipi introspezionabili la verifica del tipo parte solo quando
 * l'introspezione fallisce, per spiegare il perché (es. "risponde come
 * WFS, non come ArcGIS Vector") senza duplicare richieste sul percorso
 * felice.
 */
export function MapLayerServiceStatus(props: IMapLayerServiceStatusProps) {
  const { prefix } = props;

  const source = useWatchedMapLayerSource(prefix);
  const introspectable = isMetadataSupportedLayerType(source.type);

  const { availableAttributes, geometryType, status } =
    useMapLayerAttributes(source);
  const { diff, pruneInvalidAttributes } = useLayerAttributeReconciliation(
    prefix,
    availableAttributes,
    status === 'ok',
  );

  const detection = useMapServiceTypeValidation(source, {
    // Introspezione ok o in corso → il servizio è già (in via di)
    // conferma: la verifica tipo serve solo a spiegare i fallimenti
    enabled:
      !introspectable ||
      status === 'unreachable' ||
      status === 'invalid-response',
  });

  // Tipo non ancora scelto, URL non valido o tipo non verificabile
  // (OSM, XYZ): nessun rumore.
  if (source.type === undefined) return null;

  // Il mismatch è il messaggio più specifico: spiega sia un'introspezione
  // fallita sia una config sbagliata.
  if (detection.status === 'mismatch' && detection.result?.detectedType) {
    const detectedLabel =
      detection.result.detectedLabel ??
      getMapLayerTypeLabel(detection.result.detectedType);
    return (
      <Alert
        style={STATUS_STYLE}
        type="error"
        showIcon
        message={`Il servizio risponde come ${detectedLabel}, non come ${getMapLayerTypeLabel(source.type)}. Verificare il tipo o l'URL del layer.`}
      />
    );
  }

  if (introspectable) {
    return (
      <IntrospectionStatus
        status={status}
        availableAttributesCount={availableAttributes.length}
        geometryType={geometryType}
        diff={diff}
        pruneInvalidAttributes={pruneInvalidAttributes}
      />
    );
  }

  return <DetectionOnlyStatus status={detection.status} source={source} />;
}

interface IIntrospectionStatusProps {
  status: ReturnType<typeof useMapLayerAttributes>['status'];
  availableAttributesCount: number;
  geometryType: string | undefined;
  diff: ReturnType<typeof useLayerAttributeReconciliation>['diff'];
  pruneInvalidAttributes: () => void;
}

/** Esiti dell'introspezione attributi (tipi vettoriali). */
function IntrospectionStatus(props: IIntrospectionStatusProps) {
  const {
    status,
    availableAttributesCount,
    geometryType,
    diff,
    pruneInvalidAttributes,
  } = props;

  // idle: URL non ancora valido o config protocollo incompleta, gli
  // input attributi restano in modalità manuale
  if (status === 'idle' || status === 'unsupported-type') return null;

  if (status === 'loading') {
    return (
      <Alert
        style={STATUS_STYLE}
        type="info"
        showIcon
        message="Verifica del servizio in corso..."
      />
    );
  }

  if (status === 'service-root') {
    return (
      <Alert
        style={STATUS_STYLE}
        type="error"
        showIcon
        message="L'URL punta al servizio, non a un layer. Indicare l'URL di un singolo layer (es. .../MapServer/0)."
      />
    );
  }

  if (status === 'unreachable') {
    return (
      <Alert
        style={STATUS_STYLE}
        type="error"
        showIcon
        message="Servizio non raggiungibile. Verificare l'URL e che il servizio consenta richieste dal browser (CORS). È possibile inserire gli attributi manualmente."
      />
    );
  }

  if (status === 'invalid-response') {
    return (
      <Alert
        style={STATUS_STYLE}
        type="error"
        showIcon
        message="Il servizio ha restituito una risposta non valida. È possibile inserire gli attributi manualmente."
      />
    );
  }

  const geometrySuffix = geometryType ? ` | Geometria: ${geometryType}` : '';

  return (
    <>
      <Alert
        style={STATUS_STYLE}
        type="success"
        showIcon
        message={`${availableAttributesCount} attributi disponibili dal servizio${geometrySuffix}`}
      />
      {diff.hasInvalid && (
        <Alert
          style={STATUS_STYLE}
          type="warning"
          showIcon
          message="Alcuni attributi configurati non sono più disponibili dal servizio"
          description={diff.invalidNames.join(', ')}
          action={
            <Button size="small" onClick={pruneInvalidAttributes}>
              Reimposta attributi
            </Button>
          }
        />
      )}
    </>
  );
}

interface IDetectionOnlyStatusProps {
  status: ReturnType<typeof useMapServiceTypeValidation>['status'];
  source: ReturnType<typeof useWatchedMapLayerSource>;
}

/**
 * Esiti della sola verifica del tipo di servizio, per i tipi senza
 * introspezione attributi (WMS, ArcGIS raster/tile). Il mismatch è
 * gestito a monte.
 */
function DetectionOnlyStatus(props: IDetectionOnlyStatusProps) {
  const { status, source } = props;

  if (status === 'idle' || status === 'loading') return null;

  if (status === 'match') {
    return (
      <Alert
        style={STATUS_STYLE}
        type="success"
        showIcon
        message={`Servizio verificato: risponde come ${source.type ? getMapLayerTypeLabel(source.type) : 'atteso'}.`}
      />
    );
  }

  if (status === 'unreachable') {
    return (
      <Alert
        style={STATUS_STYLE}
        type="warning"
        showIcon
        message="Servizio non raggiungibile dal browser (URL errato, servizio non attivo o CORS): verifica del tipo non conclusiva."
      />
    );
  }

  return (
    <Alert
      style={STATUS_STYLE}
      type="warning"
      showIcon
      message="Non è stato possibile riconoscere il tipo di servizio dalla risposta: verifica non conclusiva."
    />
  );
}
